import styles from "./Modals.module.scss";
import React, { useRef, useState } from "react";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, DestinationSettings, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { LlmProviderModalStore } from "./LlmProviderModalStore";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { observer } from "mobx-react-lite";
import type { AiProvider } from "@whatslly/types/ai/llm";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";

type ApiKeyProviderType = "openai" | "anthropic" | "google";

export const LLMProviderSharedFields = <T extends AiProvider>({
  providerType,
  formikContext,
  settings,
}: {
  providerType: T;
  formikContext: React.RefObject<FormikProps<FormDestinationDataType<T>> | null>;
  settings: DestinationSettings<T> | undefined;
}) => {
  const { t } = useTranslation();
  const llmProviderModalStore = useDependencyInjector(LlmProviderModalStore);

  return (
    <>
      {llmProviderModalStore.loadModels(providerType).case({
        pending: () => <></>,
        fulfilled: (res) => {
          const models = res.success ? res.models : [];
          const defaultModel = settings?.defaultModel
            ? { label: models.find((m) => m.modelId === settings?.defaultModel)?.displayName ?? "", value: settings?.defaultModel ?? "" }
            : null;
          const enabledModels = settings?.enabledModels?.map((m) => ({ label: models.find((mm) => mm.modelId === m)?.displayName ?? m, value: m }));
          return (
            <>
              <div className={styles.field}>
                <Label className={styles.filedLabel} text={t("destinations_page.llm_enabled_models")} />
                <FormSelect<{ label: string; value: string }, true>
                  name="enabledModels"
                  selectSize="small"
                  paddingInline="none"
                  options={models.map((m) => ({ label: m.displayName, value: m.modelId }))}
                  isMulti={true}
                  isSearchable={true}
                  placeholder={t("destinations_page.llm_enabled_models_placeholder")}
                  defaultValue={enabledModels}
                  onChange={(value: { label: string; value: string }[]) => {
                    formikContext.current?.setFieldValue(
                      "enabledModels",
                      value.map((v) => v.value)
                    );
                  }}
                />
              </div>
              <div className={styles.field}>
                <Label className={styles.filedLabel} text={t("destinations_page.llm_default_model")} />
                <FormSelect<{ label: string; value: string }>
                  name="defaultModel"
                  selectSize="small"
                  paddingInline="none"
                  options={models.map((m) => ({ label: m.displayName, value: m.modelId }))}
                  isClearable={true}
                  placeholder={t("destinations_page.llm_default_model_placeholder")}
                  defaultValue={defaultModel}
                  onChange={(value: { label: string; value: string } | null) => {
                    formikContext.current?.setFieldValue("defaultModel", value?.value ?? null);
                  }}
                />
              </div>
            </>
          );
        },
      })}
    </>
  );
};

const createApiKeyProviderForm = <T extends ApiKeyProviderType>(providerType: T): React.FC<DestinationFormProps<T>> => {
  const baseData = {
    type: providerType,
    integrationType: DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER,
    settings: { apiKey: "", enabledModels: [], defaultModel: "" },
  };

  const ApiKeyProviderForm: React.FC<DestinationFormProps<T>> = observer(({ settings, onSubmitForm }) => {
    const { t } = useTranslation();
    const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
    const formikContext = useRef<FormikProps<FormDestinationDataType<T>>>(null);
    const initialValues = (settings || baseData.settings) as FormDestinationDataType<T>;
    const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(!initialValues.apiKey);

    const getFormValues = () => {
      const data = (formikContext.current?.values ?? {}) as FormDestinationDataType<T>;
      return { ...baseData, settings: data } as any;
    };

    const validate = async (values: FormDestinationDataType<T>) => {
      const errors: FormikErrors<PartialRecord<keyof FormDestinationDataType<T>, string>> = {};
      if (!values.apiKey) {
        errors.apiKey = t("field_is_required");
      }
      setFormikErrorHasErrors(Object.keys(errors).length > 0);
      return errors;
    };

    const onSubmit = async () => {
      store.testConnection(t, getFormValues());
      const res = await store.testConnectionPromise;
      if (res?.success) {
        onSubmitForm(getFormValues());
      }
    };

    return (
      <>
        <FormElement initialValues={initialValues} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.api_key")} />
            <FormPasswordTextInput name={"apiKey"} paddingInline="none" />
          </div>
          <LLMProviderSharedFields providerType={providerType} formikContext={formikContext} settings={settings} />
          <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
        </FormElement>
      </>
    );
  });

  return ApiKeyProviderForm;
};

export const OpenAiModalForm = createApiKeyProviderForm("openai");
export const AnthropicModalForm = createApiKeyProviderForm("anthropic");
export const GoogleAiModalForm = createApiKeyProviderForm("google");
